import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import {
  addWatchlistItemRequested,
  createBusinessCardSuccess,
  loadBusinessCardSuccess,
  sendBusinessCardButtonActions,
  subscribeChannelRequested,
  USER_SESSION_STATUS,
  WatchlistItemIdentity,
} from '@planone/bau365/data-access-user';
import {
  ConfirmSendBusinessCardComponent,
  SendBusinessCardRestrictedReason,
  UnauthorizedModalComponent,
} from '@planone/bau365/ui-modals';
import { SendBusinessCardModalComponent } from '@planone/bau365/ui-modals';
import { RedirectUrlGenerator } from '@planone/bau365/util-keycloak';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import { CookieConsentFacade } from '@planone/shared/feature-cookie-consent';
import { WindowRef } from '@planone/shared/util-browser-api';
import { BehaviorSubject, combineLatest, filter, merge, withLatestFrom } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { EntityIdentity, RestrictedAccessAction } from './restricted-access.models';

@Injectable()
export class RestrictedAccessEffects {
  unauthorizedModal$ = createEffect(
    () =>
      combineLatest([this.actions$.pipe(ofType(ROUTER_NAVIGATED)), this.cookieConsentFacade.accepted$]).pipe(
        withLatestFrom(this.userSessionStatus, this.fairsFacade.fairId$, this.fairsFacade.isBauInsights$),
        filter(
          ([[_action, _cookieConsent], _activeSession, _fairId, isBauInsights]) =>
            !isBauInsights &&
            !isPlatformServer(this.platformId) &&
            !this.botRegex.test(this.windowRef.nativeWindow.navigator.userAgent)
        ),
        tap(([[action, cookieConsent], activeSession, fairId]) => {
          const currentRoute = (<any>action)?.payload.routerState;
          const data = this.extractData(currentRoute);
          let newCount;

          if (data['userSessionRequired'] && !activeSession) {
            if (cookieConsent && !currentRoute.url.includes('/search')) {
              const count = JSON.parse(localStorage.getItem('freeAccessCount') ?? '0');
              localStorage.setItem('freeAccessCount', count + 1);
              newCount = JSON.parse(localStorage.getItem('freeAccessCount') ?? '');
            } else if (cookieConsent === false) {
              this.openUnauthorizedModal(fairId);
            }
          }

          if (
            !activeSession &&
            (newCount > 2 || (currentRoute.url.includes('/search') && !currentRoute.url.includes('/exhibitors/search')))
          ) {
            this.openUnauthorizedModal(fairId);
          }
        })
      ),
    { dispatch: false }
  );

  unauthorizedModalForWatchlistAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addWatchlistItemRequested),
        withLatestFrom(this.userSessionStatus, this.route.queryParams),
        filter(
          ([_item, activeSession, params]) =>
            !activeSession && !('utm_medium' in params && params['utm_medium'] === 'qr-code')
        ),
        tap(([{ watchlistItem, pageContext }, _activeSession, _params]) => {
          let redirectUri;
          if (pageContext === 'home') {
            redirectUri = {
              redirectUri: this.transformUrlForDetailPage(watchlistItem, pageContext),
            };
          } else {
            redirectUri = {
              redirectUri: this.transformUrlForCurrentPage(
                {
                  id: watchlistItem.entity_id,
                  type: watchlistItem.entity_type,
                  fairId: watchlistItem.fair_id,
                },
                { action: 'watchlist', pageContext }
              ),
            };
          }

          this.dialog.open(UnauthorizedModalComponent, {
            width: '600px',
            maxWidth: '100%',
            disableClose: false,
            panelClass: 'custom-mobile-modal',
            data: {
              fairId: watchlistItem.fair_id,
              ...(redirectUri ?? undefined),
            },
          });
        })
      ),
    { dispatch: false }
  );

  unauthorizedModalForSubscribeAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscribeChannelRequested),
        withLatestFrom(this.userSessionStatus),
        filter(([_, activeSession]) => !activeSession),
        tap(([{ fairId, channelType, id, context }, _]) => {
          const redirectUri = this.transformUrlForCurrentPage(
            { fairId, id, type: channelType },
            { action: 'subscribe', pageContext: context }
          );
          this.dialog.open(UnauthorizedModalComponent, {
            width: '600px',
            maxWidth: '100%',
            disableClose: false,
            panelClass: 'custom-mobile-modal',
            data: { fairId, redirectUri },
          });
        })
      ),
    { dispatch: false }
  );

  sendBusinessCardWithoutLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendBusinessCardButtonActions.sendBusinessCardRequested),
        withLatestFrom(this.userSessionStatus, this.fairsFacade.isBauInsights$),
        filter(([_, activeSession]) => !activeSession),
        tap(([{ fairId, exhibitorId }, , isBauInsights]) => {
          const reason: SendBusinessCardRestrictedReason = 'isNotLoggedIn';
          const redirectUri = this.urlGenerator.createRedirectUrl(['/', 'fairs', fairId, 'exhibitors', exhibitorId], {
            queryParams: { sendBusinessCard: true, fairId, exhibitorId, origin: 'keycloakAuth' },
          });
          this.dialog.open(SendBusinessCardModalComponent, {
            width: '600px',
            maxWidth: '100%',
            data: { fairId, redirectUri, reason, isBauInsights },
          });
        })
      ),
    { dispatch: false }
  );

  sendBusinessCardWithoutBusinessCard$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(sendBusinessCardButtonActions.sendBusinessCardRequested)),
        merge(
          this.actions$.pipe(ofType(loadBusinessCardSuccess), take(1)),
          this.actions$.pipe(ofType(createBusinessCardSuccess))
        ),
        this.fairsFacade.isBauInsights$,
      ]).pipe(
        withLatestFrom(this.userSessionStatus),
        filter(([[, { businessCard }], activeSession]) => activeSession && typeof businessCard === 'undefined'),
        tap(([[{ fairId, exhibitorId }, , isBauInsights]]) => {
          const redirectUri = this.urlGenerator.createRedirectUrl(['/', 'profile', 'business-card'], {
            queryParams: { sendBusinessCard: 'true', fairId, exhibitorId },
          });
          const reason: SendBusinessCardRestrictedReason = 'hasNoBusinessCard';
          this.dialog.open(SendBusinessCardModalComponent, { data: { fairId, redirectUri, reason, isBauInsights } });
        })
      ),
    { dispatch: false }
  );

  sendBusinessCardConfirm$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(sendBusinessCardButtonActions.sendBusinessCardRequested)),
      merge(
        this.actions$.pipe(ofType(loadBusinessCardSuccess), take(1)),
        this.actions$.pipe(ofType(createBusinessCardSuccess))
      ),
      this.fairsFacade.isBauInsights$,
    ]).pipe(
      withLatestFrom(
        this.userSessionStatus,
        this.route.queryParamMap.pipe(map((params) => params.get('origin') ?? 'firstTime'))
      ),
      filter(([[, { businessCard }], activeSession]) => activeSession && typeof businessCard !== 'undefined'),
      switchMap(([[props, , isBauInsights], , origin]) =>
        this.dialog
          .open(ConfirmSendBusinessCardComponent, { data: { isBauInsights, origin } })
          .afterClosed()
          .pipe(
            map((sendBusinessCard) =>
              sendBusinessCard
                ? sendBusinessCardButtonActions.sendBusinessCard(props)
                : sendBusinessCardButtonActions.sendBusinessCardRejected()
            )
          )
      )
    )
  );

  private extractData(route: SerializedRouterStateSnapshot | ActivatedRouteSnapshot | undefined | null): any {
    let child = route?.root.firstChild;
    let data = {};

    if (child) {
      while (child?.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.data };
      }
    }

    return data;
  }

  private transformUrlForDetailPage(watchlistItem: WatchlistItemIdentity, pageContext: string) {
    const isLocalhost = window.location.origin.includes('localhost');
    return `${window.location.origin}/${!isLocalhost ? this.locale : ''}/fairs/${
      watchlistItem.fair_id
    }/${this.mapEntityTypeForUrl(watchlistItem.entity_type)}/${watchlistItem.entity_id}?watchlist=1&fairId=${
      watchlistItem.fair_id
    }&entityType=${watchlistItem.entity_type}&entityId=${watchlistItem.entity_id}&pageContext=${pageContext}`;
  }

  private transformUrlForCurrentPage(entity: EntityIdentity, action: RestrictedAccessAction): string {
    const isLocalhost = window.location.origin.includes('localhost');
    const url = new URL(`${window.location.origin}/${!isLocalhost ? this.locale : ''}${this.router.url}`);
    url.searchParams.append(action.action, '1');
    url.searchParams.append('fairId', entity.fairId);
    url.searchParams.append('entityType', entity.type);
    url.searchParams.append('entityId', entity.id);
    if (action.action === 'watchlist' || action.action === 'subscribe') {
      url.searchParams.append('pageContext', action.pageContext);
    }
    return url.toString();
  }

  private mapEntityTypeForUrl(entityType: string) {
    let entity;
    switch (entityType) {
      case 'POST':
        entity = 'posts';
        break;
      case 'EXHIBITOR':
        entity = 'exhibitors';
        break;
      case 'PRODUCT':
        entity = 'products';
        break;
      case 'EVENT':
        entity = 'events';
        break;
    }
    return entity;
  }

  private openUnauthorizedModal(fairId: string) {
    this.dialog.open(UnauthorizedModalComponent, {
      width: '600px',
      maxWidth: '100%',
      disableClose: true,
      panelClass: 'custom-mobile-modal',
      data: { fairId },
    });
  }

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private dialog: MatDialog,
    private fairsFacade: DataAccessFairsFacade,
    private cookieConsentFacade: CookieConsentFacade,
    private router: Router,
    private route: ActivatedRoute,
    private windowRef: WindowRef,
    private urlGenerator: RedirectUrlGenerator,
    @Inject(USER_SESSION_STATUS) private userSessionStatus: BehaviorSubject<boolean>,
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(BOT_REGEX) private botRegex: RegExp,
    @Inject(LOCALE_ID) private locale: string
  ) {}
}
